import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';

import { useAuth } from 'context/AuthContext';
import { getMembrosEquipeGestor } from 'services/dashboard.service';
import { getLevelPlayer } from 'services/levels.service';

import ModalSairPrograma from 'components/modal-sair-programa';

import { DashboardSidebar } from './sidebar';
import { DashboardPageHeader } from './page-header';

interface ShellData {
  level: any;
  groupInfo: any;
  hasTeam: boolean;
  pendingApprovals?: number;
}

const RebrandShellContext = createContext<ShellData>({
  level: undefined,
  groupInfo: undefined,
  hasTeam: false,
  pendingApprovals: undefined,
});

/** Dados carregados uma única vez pelo shell (nível, grupo, equipe), disponíveis
 *  às páginas sem refazer as chamadas — ex.: o GroupDetailModal da Home. */
export function useRebrandShell() {
  return useContext(RebrandShellContext);
}

interface RebrandShellProps {
  title: string;
  subtitle?: string;
  /** Aplica o fundo escuro do design (telas como o Extrato). */
  dark?: boolean;
  children: ReactNode;
}

export function RebrandShell({ title, subtitle, dark, children }: RebrandShellProps) {
  const { user, userInfo, signOut, isMSTeams } = useAuth();
  const { instance } = useMsal();

  const [level, setLevel] = useState<any>();
  const [hasTeam, setHasTeam] = useState(false);
  const [pendingApprovals] = useState<number>();

  const groupInfo = useMemo(() => {
    if (!userInfo?.grupo) return undefined;
    return { ro: userInfo.ro, grupo_definido: userInfo.grupo_definido, ...userInfo.grupo };
  }, [userInfo]);

  useEffect(() => {
    async function loadLevel() {
      try {
        const data = await getLevelPlayer();
        setLevel(data);
      } catch (e) {}
    }

    if (user) loadLevel();
  }, [user]);

  useEffect(() => {
    async function loadTeam(info: any) {
      const isGestor =
        info.tipo_ro === 'GERENCIAL' ||
        info.funcao_localizacao === 3001 ||
        info.funcao_localizacao === 3002 ||
        info.funcao_localizacao === 3003;

      if (!isGestor) return;

      try {
        const data = await getMembrosEquipeGestor(info._id);
        if (data?.result?.length) setHasTeam(true);
      } catch (e) {}
    }

    if (userInfo && user) loadTeam(userInfo);
  }, [userInfo, user]);

  const [sairAberto, setSairAberto] = useState(false);

  function handleLogout() {
    instance.logoutRedirect({ onRedirectNavigate: () => false });
    signOut();
  }

  function handleNavMobile() {
    document.dispatchEvent(new CustomEvent('openNavMobile'));
  }

  /* Encerra a sessão em vez de só recarregar o usuário: quem acabou de pedir
     para sair não deveria continuar navegando no programa. O logout é local —
     o Funifier não revoga token emitido —, então a barreira de verdade continua
     sendo a tela de saída, que aparece se a pessoa entrar de novo.
     Em MS Teams não se chama logoutRedirect: o embed cuida da autenticação e é
     por isso que o botão Sair nem aparece lá. */
  function handleSaiu() {
    setSairAberto(false);

    if (isMSTeams) {
      signOut();
      return;
    }

    handleLogout();
  }

  if (!user) return null;

  return (
    <RebrandShellContext.Provider value={{ level, groupInfo, hasTeam, pendingApprovals }}>
      <div className="rb-shell">
        <DashboardSidebar hasTeam={hasTeam} pendingApprovals={pendingApprovals} />

        <main className={`rb-main ${dark ? 'rb-main--dark' : ''}`}>
          <DashboardPageHeader
            title={title}
            subtitle={subtitle}
            user={user}
            userInfo={userInfo}
            level={level}
            groupInfo={groupInfo}
            showLogout={!isMSTeams}
            onLogout={handleLogout}
            onOpenNavMobile={handleNavMobile}
            onSairDoPrograma={() => setSairAberto(true)}
          />

          {children}

          {sairAberto && (
            <ModalSairPrograma callback={acao => (acao === 'saiu' ? handleSaiu() : setSairAberto(false))} />
          )}
        </main>
      </div>
    </RebrandShellContext.Provider>
  );
}
